import { Box, Typography } from '@mui/material';
import { DownloadIcon, UploadIcon } from 'lucide-react';
import { ChangeEvent, useCallback, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useRecoilValue } from 'recoil';
import WorkspaceLogo from '~/components/Logo/WorkspaceLogo';
import useDocumentDragDetect from '~/hooks/useDocumentDragDetect';
import useProcessDicomFiles from '~/hooks/useProcessDicomFiles';
import useViewportEnvironment from '~/hooks/useViewportEnvironment';
import { selectedPatientIdState } from '~/state/atoms/patientAtoms';

function FilesDraggedContent() {
    return (
        <Box display="flex" flexDirection="column" alignItems="center">
            <DownloadIcon size={48} />
            <Box mb={2} mt={1}>
                <Typography variant="h5" textAlign="center">
                    <FormattedMessage id="uploadDICOMFilesDragging" />
                </Typography>
                <Typography
                    color={(theme) => theme.palette.text.secondary}
                    fontSize={14}
                    textAlign="center"
                    width={500}
                >
                    <FormattedMessage id="rtviewerDescription" />
                </Typography>
            </Box>
            <Typography textAlign="center">
                <FormattedMessage id="dragAndDropInstruction" />
            </Typography>
        </Box>
    );
}

function WaitingForFilesContent() {
    return (
        <Box display="flex" flexDirection="column" alignItems="center">
            <UploadIcon size={48} />
            <Box mb={2} mt={1}>
                <Typography variant="h5" textAlign="center">
                    <FormattedMessage id="uploadDICOMFilesWaiting" />
                </Typography>
                <Typography
                    color={(theme) => theme.palette.text.secondary}
                    fontSize={14}
                    textAlign="center"
                    width={500}
                >
                    <FormattedMessage id="rtviewerDescription" />
                </Typography>
            </Box>
            <Typography textAlign="center">
                <FormattedMessage id="dragAndDropInstruction" />
            </Typography>
        </Box>
    );
}

export default function DNDArea() {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [isDragOver, setIsDragOver] = useState<boolean>(false);
    const selectedPatientId = useRecoilValue(selectedPatientIdState);
    const { initializeEnvironment } = useViewportEnvironment();
    const { processImportedFiles } = useProcessDicomFiles();

    const handleFileChange = useCallback(
        async (e: ChangeEvent<HTMLInputElement>) => {
            e.preventDefault();
            e.stopPropagation();
            if (e.target.files && e.target.files[0]) {
                const files = Array.from(e.target.files);
                const processedItems = await processImportedFiles(files);
                if (processedItems) initializeEnvironment(processedItems);
            }
        },
        [processImportedFiles]
    );

    const handleDrop = useCallback(
        async (e: DragEvent) => {
            e.preventDefault();
            e.stopPropagation();
            setIsDragOver(false);
            if (e.dataTransfer?.files && e.dataTransfer.files.length > 0) {
                const files = Array.from(e.dataTransfer.files);
                const processedItems = await processImportedFiles(files);
                if (processedItems) initializeEnvironment(processedItems);
            }
        },
        [processImportedFiles]
    );

    const handleDragLeave = useCallback((e: DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        const dropArea = e.currentTarget as HTMLElement;
        const relatedTarget = e.relatedTarget as HTMLElement;
        // Check if the relatedTarget is a child of the dropArea
        if (!dropArea.contains(relatedTarget)) {
            setIsDragOver(false);
        }
    }, []);

    const handleDragOver = useCallback(() => {
        setIsDragOver(true);
    }, []);

    useDocumentDragDetect({
        onDrop: handleDrop,
        onDragLeave: handleDragLeave,
        onDragEnter: handleDragOver,
    });

    const isActive = isDragOver || !selectedPatientId;

    return (
        <Box
            position="absolute"
            overflow="hidden"
            top={0}
            left={0}
            zIndex={isDragOver ? 9999 : 900}
            width="calc(100% + 10px)"
            height="calc(100% + 10px)"
            sx={{
                pointerEvents: isActive ? 'auto' : 'none',
            }}
        >
            <Box visibility={isActive ? 'visible' : 'hidden'}>
                <input
                    type="file"
                    multiple
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                    ref={fileInputRef}
                />
                <Box
                    position="absolute"
                    width="100%"
                    height="100%"
                    px={1}
                    bgcolor="rgba(0,0,0,0.4)"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    onClick={() => fileInputRef.current?.click()}
                >
                    <Box position="relative" sx={{ pointerEvents: 'none' }}>
                        <Box
                            position="absolute"
                            top={-100}
                            display="flex"
                            justifyContent="center"
                            width="100%"
                        >
                            <WorkspaceLogo height={80} />
                        </Box>
                        <Box
                            p={6}
                            border={(theme) =>
                                `1px dashed ${theme.palette.primary.main}`
                            }
                            borderRadius={1}
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            justifyContent="center"
                            bgcolor={(theme) =>
                                isDragOver
                                    ? theme.palette.primary.main
                                    : theme.palette.background.paper
                            }
                        >
                            {isDragOver ? (
                                <FilesDraggedContent />
                            ) : (
                                <WaitingForFilesContent />
                            )}
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}
